import { z } from "zod";
import {
  EvidenceBundleSchema,
  type EvidenceBundle,
  type EvidenceFact,
  type EvidenceSourceRef,
} from "./evidence";
import type { AcquisitionPurpose } from "./evidence-acquisition";

/**
 * **紧凑读数**：让模型只报"数"，元数据由服务端机械补齐（用户 2026-09-18 批准 A/B）。
 *
 * 为什么要有它（实测出来的账）：一条事实在契约里要带
 * `factId/field/value/timeScope/precision/origin/inferred/sourceRef/entityId` 八个字段，
 * 一张搜索页 10 个商品 × 4 条事实 = 40 条 ⇒ **约 21,000 字符**的输出，
 * 而其中每个商品真正不同的只有名字、价格、已售。实测生成速度 35–90 字符/秒 ⇒
 * **一份结果 40 秒到 4 分钟**。
 *
 * 这些字段对同一个商品**完全一样**，所以让模型逐条重抄一遍既慢又容易出错（真机出现过
 * `precision: "unknown"` 导致整段被拒）。改成"模型只报 `n/p/s`，服务端把同一份元数据套上去"：
 * 输出缩到 ~500 字符，而**来源、精度、出处一条不少**——展开是**机械的**，不编任何内容。
 */

/** 一个商品一行。字段名短到极致：省下的都是模型的输出时间。 */
export const CompactProductSchema = z.object({
  /** 商品名（照抄图上文字）。 */
  n: z.string().trim().min(1).max(300),
  /** 售价（数字；币种由整份读数里的 `cur` 给一次）。 */
  p: z.number().finite().positive().optional(),
  /** 已售件数（整数；「1.4K+」取下界 1400）。 */
  s: z.number().int().nonnegative().optional(),
  /** 已售原文，必须保留 K/M/万/+；存在时按它机械换算，不信任模型心算。 */
  // Empty means explicitly unreadable, not legacy absence: it must suppress s,
  // rather than reject the other products or turn a missing sale count into zero.
  st: z.string().trim().max(120).optional(),
  /** 详情页链接（只有评论那条要"逐个进详情"时才问）。 */
  l: z.string().trim().max(2048).optional(),
  /** 这条读自第几张图（从 1 开始；只发一张图时可以省略）。 */
  i: z.number().int().min(1).max(2).optional(),
}).strict();

/** 一条评价标签一行。 */
export const CompactReviewTagSchema = z.object({
  /** 标签原文（照抄）。 */
  t: z.string().trim().min(1).max(120),
  /** 这个标签的**中文意思**（用户口径 2026-09-18：最终结论要中文）。 */
  z: z.string().trim().min(1).max(120).optional(),
  /** 正负向：positive / negative / neutral。 */
  k: z.enum(["positive", "negative", "neutral"]).optional(),
  /** 该标签条数。 */
  c: z.number().int().nonnegative().optional(),
  i: z.number().int().min(1).max(2).optional(),
}).strict();

export const CompactEvidenceSchema = z.object({
  field: z.string().trim().min(1).max(120),
  value: z.union([z.string().max(2000), z.number().finite(), z.boolean(), z.null()]),
  unit: z.string().trim().min(1).max(40).optional(),
  missingReason: z.string().trim().min(1).max(200).optional(),
  /** 曲线方向、总结属于解释；图上的逐字文字/明示数字不属于解释。 */
  interpretation: z.boolean().optional(),
  i: z.number().int().min(1).max(2).optional(),
}).strict().superRefine((value, context) => {
  if ((value.value === null) !== Boolean(value.missingReason)) {
    context.addIssue({ code: "custom", path: ["missingReason"], message: "仅空值必须给出缺失原因" });
  }
});

export const CompactReadingSchema = z.object({
  /** 币种标记，整份读数给一次（如 `US$`）；读不到就不给。 */
  cur: z.string().trim().max(12).optional(),
  /** 商品清单（竞争 / 价格带）。 */
  items: z.array(CompactProductSchema).max(24).optional(),
  /** 评价标签（评论）。 */
  tags: z.array(CompactReviewTagSchema).max(60).optional(),
  evidence: z.array(CompactEvidenceSchema).max(40).optional(),
  /** 这次读到了多少（用于 coverage；不填就按数组长度算）。 */
  seen: z.number().int().nonnegative().max(999).optional(),
  /** 图上还有没读完的（截断/屏幕外）。 */
  more: z.boolean().optional(),
  /** 一句如实说明（例如"只读了首屏可见的 10 个"）。 */
  note: z.string().trim().max(300).optional(),
}).strict();
export type CompactReading = z.infer<typeof CompactReadingSchema>;
export type CompactReadingKind = "products" | "review_tags" | "evidence";

/** 按请求模式严格校验；错误必须停留在业务读数字段，不能回退报缺整包元数据。 */
export function compactReadingSchema(kind: CompactReadingKind) {
  const common = CompactReadingSchema.pick({ seen: true, more: true, note: true });
  if (kind === "products") return common.extend({ cur: CompactReadingSchema.shape.cur, items: z.array(CompactProductSchema).max(24) });
  if (kind === "review_tags") return common.extend({ tags: z.array(CompactReviewTagSchema).max(60) });
  return common.extend({ evidence: z.array(CompactEvidenceSchema).max(40) });
}

export function compactReadingJsonSchema(kind: CompactReadingKind): Record<string, unknown> {
  return z.toJSONSchema(compactReadingSchema(kind)) as Record<string, unknown>;
}

/**
 * 判一份模型输出是不是紧凑读数（不是就走原来那条整包路径，互不影响）。
 *
 * **空数组也算**（`{"items":[]}`＝"这一屏一个商品都没有读到"）：那是**如实结果**，
 * 必须展开成"读到 0 条"的证据包，而不是回退到整包解析（那会把一句实话变成 `OUTPUT_INVALID`）。
 */
export function isCompactReading(raw: unknown): boolean {
  const parsed = CompactReadingSchema.safeParse(raw);
  if (!parsed.success) return false;
  return Array.isArray(parsed.data.items) || Array.isArray(parsed.data.tags) || Array.isArray(parsed.data.evidence);
}

/** 浏览器和服务端共用的稳定标识哈希，仅用于命名空间，不用于安全校验。 */
function imageNamespace(imageIds: readonly string[]): string {
  let hash = 0xcbf29ce484222325n;
  for (const byte of new TextEncoder().encode(JSON.stringify(imageIds))) {
    hash = BigInt.asUintN(64, (hash ^ BigInt(byte)) * 0x100000001b3n);
  }
  return `compact-${hash.toString(16).padStart(16, "0")}`;
}

/** 只换算明确销量写法，不从描述或多组数字里猜数。 */
function soldCountFromText(text: string): number | undefined {
  const match = /^(?:sold\s*:?\s*|已售\s*)?(\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?)\s*([km万千]?)\s*(?:\+|以上)?\s*(?:sold|件已售|已售|件)?$/i.exec(text.trim());
  if (!match) return undefined;
  const multiplier: Record<string, number> = { k: 1000, m: 1000000, 万: 10000, 千: 1000 };
  const count = Number(match[1]!.replaceAll(",", "")) * (multiplier[match[2]!.toLowerCase()] ?? 1);
  return Number.isSafeInteger(count) && count >= 0 ? count : undefined;
}

/**
 * 紧凑读数 → **完整证据包**（机械展开）。
 *
 * 展开规则（每一处都不含判断）：
 * - 事实：`n` → `product_name`，`p` → `product_price`（`unit` 用整份读数的 `cur`），
 *   `s` → `product_sold`，`l` → `product_link`；标签同理（`t/k/c` → `review_tag/review_polarity/review_count`）；
 * - 出处：**每张图一条** `image_region`（bbox 是整张图 `{0,0,1,1}`——展开不知道具体框，也不假装知道），
 *   事实的 `sourceRef` 指向它自己那张图的出处；
 * - 精度与来路：图里读到的原文/数字是 screenshot + confirmed_ocr；
 *   翻译、分类、趋势解释是 model_inference，通过 entityId 关联原始读数，不冒充 OCR；
 * - 时间：整份读数共用一个 `snapshot` 时间（读数没有统计周期概念，周期的口径仍在会话目标里）。
 */
export function expandCompactReading(raw: unknown, input: {
  purpose: AcquisitionPurpose;
  /** 本次共享的图片 id（按顺序）；`i` 就是这里的下标 + 1。 */
  imageIds: readonly string[];
  /** 采集时刻（由调用方给，便于测试）。 */
  now: string;
}): EvidenceBundle | undefined {
  const parsed = CompactReadingSchema.safeParse(raw);
  if (!parsed.success) return undefined;
  const reading = parsed.data;
  const imageIds = [...input.imageIds];
  if (imageIds.length === 0 || [...(reading.items ?? []), ...(reading.tags ?? []), ...(reading.evidence ?? [])].some(item => (item.i ?? 1) > imageIds.length)) return undefined;
  // 时间和数组序号不能标识一张图：两轮/多商品会在同一毫秒或相同序号开始。
  // 本次共享图片标识决定整份命名空间，所有交叉引用使用同一前缀。
  const namespace = imageNamespace(imageIds);
  const snapshotId = `${namespace}-snapshot`;
  const bundleId = `${namespace}-bundle`;
  const timeScope = { kind: "snapshot" as const, observedAt: input.now };

  const imageIndexOf = (value: number | undefined): number => {
    const index = (value ?? 1) - 1;
    return index;
  };
  const sources: EvidenceSourceRef[] = imageIds.map((imageId, index) => ({
    sourceRef: `${namespace}-img-${index + 1}-list`,
    snapshotId,
    collectedAt: input.now,
    location: { kind: "image_region", imageId, bbox: { x: 0, y: 0, width: 1, height: 1 } },
    precision: "confirmed_ocr",
  }));
  const sourceRefOf = (value: number | undefined): string => sources[imageIndexOf(value)]!.sourceRef;

  const facts: EvidenceFact[] = [];
  const push = (fact: Omit<EvidenceFact, "timeScope" | "precision" | "origin" | "inferred" | "sourceRef"> & { imageIndex?: number }): void => {
    const { imageIndex, ...rest } = fact;
    facts.push({
      ...rest,
      timeScope,
      precision: "confirmed_ocr",
      origin: "screenshot",
      inferred: false,
      sourceRef: sourceRefOf(imageIndex),
    });
  };
  const pushInterpretation = (fact: Omit<EvidenceFact, "timeScope" | "precision" | "origin" | "inferred" | "sourceRef">): void => {
    facts.push({ ...fact, timeScope, precision: "approximate", origin: "model_inference", inferred: true });
  };

  for (const [index, item] of (reading.items ?? []).entries()) {
    const entityId = `${namespace}-product-${index + 1}`;
    push({ factId: `${entityId}-name`, field: "product_name", value: item.n, entityId, ...(item.i ? { imageIndex: item.i } : {}) });
    if (item.p !== undefined) {
      push({ factId: `${entityId}-price`, field: "product_price", value: item.p, entityId, ...(reading.cur ? { unit: reading.cur } : {}), ...(item.i ? { imageIndex: item.i } : {}) });
    }
    const sold = item.st !== undefined ? soldCountFromText(item.st) : item.s;
    if (sold !== undefined) {
      push({ factId: `${entityId}-sold`, field: "product_sold", value: sold, entityId, ...(item.i ? { imageIndex: item.i } : {}) });
    }
    if (item.st !== undefined) {
      push({ factId: `${entityId}-sold-text`, field: "product_sold_text", value: item.st, entityId, ...(item.i ? { imageIndex: item.i } : {}) });
    }
    if (item.l) {
      push({ factId: `${entityId}-link`, field: "product_link", value: item.l, entityId, ...(item.i ? { imageIndex: item.i } : {}) });
    }
  }
  for (const [index, tag] of (reading.tags ?? []).entries()) {
    const entityId = `${namespace}-tag-${index + 1}`;
    push({ factId: `${entityId}-text`, field: "review_tag", value: tag.t, entityId, ...(tag.i ? { imageIndex: tag.i } : {}) });
    if (tag.z) {
      pushInterpretation({ factId: `${entityId}-zh`, field: "review_tag_zh", value: tag.z, entityId });
    }
    pushInterpretation({ factId: `${entityId}-polarity`, field: "review_polarity", value: tag.k ?? null, entityId, ...(!tag.k ? { missingReason: "评论标签分类补全未完成" } : {}) });
    if (!tag.z) pushInterpretation({ factId: `${entityId}-zh`, field: "review_tag_zh", value: null, entityId, missingReason: "评论标签中文翻译补全未完成" });
    if (tag.c !== undefined) {
      push({ factId: `${entityId}-count`, field: "review_count", value: tag.c, entityId, ...(tag.i ? { imageIndex: tag.i } : {}) });
    }
  }

  for (const [index, item] of (reading.evidence ?? []).entries()) {
    const { i, interpretation, ...business } = item;
    const fact = { ...business, factId: `${namespace}-evidence-${index + 1}`, entityId: `${namespace}-image-${i ?? 1}` };
    if (interpretation) pushInterpretation(fact);
    else push({ ...fact, ...(i ? { imageIndex: i } : {}) });
  }

  const itemsRead = reading.seen ?? (reading.items?.length ?? reading.tags?.length ?? reading.evidence?.length ?? 0);
  const incomplete = reading.tags?.some(tag => !tag.z || !tag.k);
  const bundle = {
    bundleId,
    purpose: input.purpose,
    facts,
    entities: [
      ...(reading.items ?? []).map((item, index) => ({ entityId: `${namespace}-product-${index + 1}`, kind: "product" as const, sourceRefs: [sourceRefOf(item.i)] })),
      ...(reading.tags ?? []).map((tag, index) => ({ entityId: `${namespace}-tag-${index + 1}`, kind: "review" as const, sourceRefs: [sourceRefOf(tag.i)] })),
      ...(reading.evidence ? imageIds.map((_, index) => ({ entityId: `${namespace}-image-${index + 1}`, kind: "page" as const, sourceRefs: [sources[index]!.sourceRef] })) : []),
    ],
    sources,
    componentStates: [],
    coverage: {
      scope: "current_view" as const,
      pagesRead: 1,
      itemsRead,
      itemsDeduped: 0,
      truncated: reading.more ?? false,
      totalKnown: false,
      ...(incomplete ? { note: `评论标签补全未完成，已保留标签原文。${reading.note ?? ""}`.slice(0, 300) } : reading.note ? { note: reading.note } : {}),
    },
    conflicts: [],
    createdAt: input.now,
  };
  const validated = EvidenceBundleSchema.safeParse(bundle);
  return validated.success ? validated.data : undefined;
}
